import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Share2, MessageCircle, Facebook, Twitter, Check, ArrowRight, Zap, Sparkles } from "lucide-react";

const shareText = (title) =>
  `Semnez petiția „${title}” pe TatiSiSofia.ro. Niciun copil nu ar trebui să fie rupt de un părinte care îl iubește. Semnează și tu:`;

const shareUrl = (slug, refCode) => {
  const base = `${typeof window !== "undefined" ? window.location.origin : "https://tatisisofia.ro"}/petitii/${slug}`;
  return refCode ? `${base}?ref=${refCode}` : base;
};

const fmtRO = (n) => (n || 0).toLocaleString("ro-RO");

/**
 * Featured Petition — 1-click sign card for homepage.
 * - Loads top active petition.
 * - Form: first name + email (no auth required).
 * - On success: pre-filled WhatsApp/Facebook/Twitter share buttons.
 * - Live progress bar with poll every 30s + animated update on sign.
 */
export const FeaturedPetition = ({ slugOverride }) => {
  const [petition, setPetition] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [myRefCode, setMyRefCode] = useState(null);
  const pollRef = useRef(null);

  // Capture incoming ?ref= from URL once on mount
  const incomingRef = (() => {
    try {
      if (typeof window === "undefined") return null;
      const q = new URLSearchParams(window.location.search);
      return q.get("ref") || null;
    } catch {
      return null;
    }
  })();

  const fetchTop = async () => {
    try {
      if (slugOverride) {
        const { data } = await api.get(`/petitions/${slugOverride}`);
        setPetition(data);
      } else {
        const { data } = await api.get("/petitions");
        if (data && data.length) setPetition(data[0]);
      }
    } catch {
      // noop
    }
  };

  // Try to fetch user's own ref_code (so shares earn them points if logged in)
  const fetchMyRef = async () => {
    try {
      const { data } = await api.get("/reputation/me");
      if (data?.authenticated && data.ref_code) setMyRefCode(data.ref_code);
    } catch {
      // guest — no ref code
    }
  };

  useEffect(() => {
    fetchTop();
    fetchMyRef();
    pollRef.current = setInterval(fetchTop, 30000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugOverride]);

  const submit = async (e) => {
    e.preventDefault();
    if (signed) return;
    if (form.name.trim().length < 2) {
      toast.error("Introdu prenumele tău");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Introdu o adresă de email validă");
      return;
    }
    setSigning(true);
    try {
      const { data } = await api.post(`/petitions/${petition.slug}/sign`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        public: true,
        ref_code: incomingRef || undefined,
      });
      if (data?.petition) setPetition(data.petition);
      setSigned(true);
      // Refresh own ref code (user might have just been auto-created earlier; not in our flow but safe)
      fetchMyRef();
      toast.success("Mulțumim! Semnătura ta a fost înregistrată.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Eroare la semnare.";
      if (typeof msg === "string" && msg.toLowerCase().includes("deja")) {
        setSigned(true);
        toast.info("Ai semnat deja această petiție. Mulțumim!");
      } else {
        toast.error(msg);
      }
    } finally {
      setSigning(false);
    }
  };

  if (!petition) return null;
  const pct = Math.min(100, ((petition.current_signatures || 0) / Math.max(1, petition.goal_signatures || 1)) * 100);
  const url = shareUrl(petition.slug, myRefCode);
  const text = shareText(petition.title);
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const twHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;

  return (
    <section
      data-testid="featured-petition"
      className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-8"
    >
      <div className="dense-card relative overflow-hidden p-5 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        {/* Decorative pulse */}
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-25 pointer-events-none blur-3xl"
          style={{ background: "radial-gradient(circle, #E89BB8 0%, transparent 70%)" }}
        />

        {/* LEFT: Petition title + progress + meta */}
        <div className="lg:col-span-7 relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-[#E89BB8] bg-[#E89BB8]/10 border border-[#E89BB8]/30 px-2.5 py-1 rounded-sm">
              <Zap className="w-3 h-3" /> Petiție prioritară
            </span>
            <span className="text-[10px] uppercase tracking-widest text-[#6C7A9C] flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3BA76D] animate-pulse" /> Activă
            </span>
          </div>

          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-[#F8F8F4] leading-[1.15]">
            {petition.title}
          </h2>

          <p className="mt-3 text-[13.5px] md:text-[14px] text-[#A0AABF] leading-relaxed line-clamp-3 max-w-2xl">
            {petition.problem_statement}
          </p>

          {/* Progress */}
          <div className="mt-5">
            <div className="flex items-end justify-between text-[12px] mb-1.5">
              <div>
                <span className="font-display text-2xl md:text-3xl gold-text" data-testid="petition-count">
                  {fmtRO(petition.current_signatures)}
                </span>
                <span className="text-[#A0AABF] ml-1.5">semnături</span>
              </div>
              <span className="text-[11px] text-[#6C7A9C]">obiectiv {fmtRO(petition.goal_signatures)}</span>
            </div>
            <div className="h-2.5 bg-[#0A1020] rounded-full overflow-hidden border border-white/5">
              <div
                className="h-full bg-gradient-to-r from-[#D4A63D] via-[#F2C053] to-[#E89BB8] transition-all duration-700 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[#A0AABF]">
              {pct.toFixed(1)}% spre obiectiv · {fmtRO(Math.max(0, (petition.goal_signatures || 0) - (petition.current_signatures || 0)))} de semnături rămase
            </div>
          </div>

          <Link
            to={`/petitii/${petition.slug}`}
            data-testid="petition-read-more"
            className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0]"
          >
            CITEȘTE DETALII <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* RIGHT: 1-click sign form or share buttons */}
        <div className="lg:col-span-5 relative">
          <div className="bg-[#0A1020]/70 border border-white/10 rounded-lg p-5 md:p-6">
            {!signed ? (
              <>
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#D4A63D] mb-2">
                  Semnează în 1 click
                </div>
                <h3 className="font-display text-lg md:text-xl text-[#F8F8F4] leading-snug mb-1">
                  Adaugă-ți vocea acum.
                </h3>
                <p className="text-[12px] text-[#A0AABF] mb-4">
                  Fără cont. Doar prenume + email — primești o confirmare.
                </p>

                <form onSubmit={submit} className="space-y-2.5">
                  <input
                    data-testid="petition-quick-name"
                    type="text"
                    required
                    placeholder="Prenume"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3.5 py-2.5 text-sm text-[#F8F8F4] placeholder-white/35 focus:border-[#D4A63D] outline-none"
                  />
                  <input
                    data-testid="petition-quick-email"
                    type="email"
                    required
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3.5 py-2.5 text-sm text-[#F8F8F4] placeholder-white/35 focus:border-[#D4A63D] outline-none"
                  />
                  <button
                    data-testid="petition-quick-submit"
                    type="submit"
                    disabled={signing}
                    className="w-full btn-gold rounded-sm py-3 text-[12px] font-bold tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {signing ? (
                      "SE SEMNEAZĂ..."
                    ) : (
                      <>
                        <Heart className="w-4 h-4 fill-current" /> SEMNEZ PETIȚIA
                      </>
                    )}
                  </button>
                </form>
                <p className="text-[10px] text-[#6C7A9C] mt-3 leading-snug">
                  Email-ul nu va fi publicat. Vei primi un email cu detalii și actualizări.
                </p>
              </>
            ) : (
              <div data-testid="petition-signed-state">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#3BA76D]/20 border border-[#3BA76D]/40 grid place-items-center">
                    <Check className="w-5 h-5 text-[#3BA76D]" />
                  </div>
                  <div>
                    <div className="font-display text-base text-[#F8F8F4]">Mulțumim!</div>
                    <div className="text-[11px] text-[#A0AABF]">Semnătura ta a fost înregistrată.</div>
                  </div>
                </div>
                <p className="text-[12.5px] text-[#F8F8F4]/90 mb-2">
                  Acum ajută-ne să mobilizăm. <strong className="rose-text">Distribuie</strong> petiția — fiecare share contează.
                </p>
                {myRefCode && (
                  <div data-testid="share-earn-hint" className="text-[10.5px] text-[#D4A63D] bg-[#D4A63D]/8 border border-[#D4A63D]/25 rounded-sm px-2.5 py-1.5 mb-3 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    <span>Câștigi <strong>+5 puncte</strong> pentru fiecare prieten care semnează prin link-ul tău (cod {myRefCode}).</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => api.post("/reputation/share", { target_type: "petition", target_slug: petition.slug, channel: "whatsapp" }).catch(() => {})}
                    data-testid="share-whatsapp"
                    className="flex flex-col items-center gap-1.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 rounded-sm py-3 transition-colors"
                  >
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                    <span className="text-[10px] font-bold tracking-widest text-[#F8F8F4]">WHATSAPP</span>
                  </a>
                  <a
                    href={fbHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => api.post("/reputation/share", { target_type: "petition", target_slug: petition.slug, channel: "facebook" }).catch(() => {})}
                    data-testid="share-facebook"
                    className="flex flex-col items-center gap-1.5 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border border-[#1877F2]/30 rounded-sm py-3 transition-colors"
                  >
                    <Facebook className="w-5 h-5 text-[#1877F2]" />
                    <span className="text-[10px] font-bold tracking-widest text-[#F8F8F4]">FACEBOOK</span>
                  </a>
                  <a
                    href={twHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => api.post("/reputation/share", { target_type: "petition", target_slug: petition.slug, channel: "twitter" }).catch(() => {})}
                    data-testid="share-twitter"
                    className="flex flex-col items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/15 rounded-sm py-3 transition-colors"
                  >
                    <Twitter className="w-5 h-5 text-[#F8F8F4]" />
                    <span className="text-[10px] font-bold tracking-widest text-[#F8F8F4]">X / TWITTER</span>
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: petition.title, text, url }).catch(() => {});
                    } else if (navigator.clipboard) {
                      navigator.clipboard.writeText(`${text} ${url}`);
                      toast.success("Link-ul a fost copiat");
                    }
                  }}
                  data-testid="share-native"
                  className="mt-3 w-full btn-gold-outline rounded-sm py-2.5 text-[11px] font-bold tracking-widest inline-flex items-center justify-center gap-2"
                >
                  <Share2 className="w-3.5 h-3.5" /> ALTĂ APLICAȚIE
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedPetition;
